import React from "react";
import { observer } from "mobx-react";
import { computed } from "mobx";
import { generatePath, withRouter, RouteComponentProps } from "react-router";
import InfiniteScroll from "react-infinite-scroll-component";

import AddIcon from "@material-ui/icons/Add";
import { Button } from "@material-ui/core";

import PatternStore from "stores/PatternStore";

import Page from "components/Layout/Page";
import Placeholder from "components/Placeholder";
import FilteringControl from "components/FilteringControl";
import SortingControl from "components/SortingControl";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import NavBar from "../NavBar";

import { SNACKBAR_TYPES } from "components/Snackbar";

import ROUTES from "navigation/routes";

import { TableSortType } from "stores/templates/TableStore";

import {
  PageControl,
  SearchContainer,
  SearchControls,
  SearchControlWrapper,
  Search,
  PatternCardWrapper,
  OverflowLoader,
  ScrollLoader,
} from "./styles";

import PatternCard from "./PatternCard";

type Props = RouteComponentProps & GlobalPopupProps & SnackbarContextProps & {};

const HEADER_WITH_TITLE_HEIGHT = "338px";
const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class Pattern extends React.Component<Props> {
  @computed get patterns() {
    return PatternStore.items;
  }

  @computed get patternListIsEmpty() {
    return this.patterns.length === 0;
  }

  componentDidMount() {
    PatternStore.fetch();
  }

  componentWillUnmount() {
    PatternStore.resetPage();
  }

  fetchMorePattern = async () => {
    const { setMessage } = this.props;

    try {
      await PatternStore.fetchMorePatterns();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    PatternStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH && PatternStore.fetch();
  };

  onSearchClear = () => {
    PatternStore.clearSearch();
    PatternStore.fetch();
  };

  onSortApply = (value: TableSortType) => {
    PatternStore.applyOrder(value);
    PatternStore.resetPage();
    PatternStore.fetch();
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      PatternStore.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    PatternStore.resetPage();
    PatternStore.fetch();
  };

  onFilterClear = () => {
    PatternStore.clearFilters();
    PatternStore.fetch();
  };

  onAddPatternClick = () => {
    this.props.history.push({
      pathname: generatePath(ROUTES.CONTENT_PATTERNS_PATTERNS_LINK_DETAILS),
      state: { editMode: true },
    });
  };

  onDetailsClick = (pattern: any) => () => {
    const path = generatePath(ROUTES.CONTENT_PATTERNS_PATTERNS_LINK_DETAILS, {
      patternId: pattern.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: false } });
  };

  onEdit = (pattern: any) => () => {
    const path = generatePath(ROUTES.CONTENT_PATTERNS_PATTERNS_LINK_DETAILS, {
      patternId: pattern.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: true } });
  };

  onPublish = (pattern: any) => async () => {
    const { setMessage } = this.props;
    const { id, publishStatus } = pattern;

    try {
      await PatternStore.changePatternStatus(id, publishStatus);
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  render() {
    return (
      <Page
        title="Patterns"
        topControls={<NavBar />}
        controls={
          <>
            <PageControl>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={this.onAddPatternClick}
              >
                Add Pattern
              </Button>
            </PageControl>
          </>
        }
        isLoading={PatternStore.isLoading}
      >
        <SearchContainer>
          <Search
            initValue={PatternStore.search}
            onApply={this.onSearchApply}
            onClear={this.onSearchClear}
          />
          <SearchControls>
            <SearchControlWrapper>
              <SortingControl
                initValue={PatternStore.orderContext.appliedOrderInfo}
                onApply={this.onSortApply}
              />
            </SearchControlWrapper>
            <SearchControlWrapper>
              <FilteringControl
                initValue={PatternStore.filters}
                onApply={this.onFilterApply}
                onClear={this.onFilterClear}
                statusFiltering
              />
            </SearchControlWrapper>
          </SearchControls>
        </SearchContainer>

        {(PatternStore.isPatternPublishing ||
          PatternStore.isPatternCopying) && <OverflowLoader />}
        {!this.patternListIsEmpty ? (
          <InfiniteScroll
            dataLength={this.patterns.length}
            hasMore={PatternStore.hasMoreInfo}
            next={this.fetchMorePattern}
            height={`calc(100vh - ${HEADER_WITH_TITLE_HEIGHT})`}
            loader={<ScrollLoader />}
          >
            {this.patterns.map(pattern => (
              <PatternCardWrapper key={pattern.id}>
                <PatternCard
                  title={pattern.title}
                  publishStatus={pattern.publishStatus}
                  onDetailsClick={this.onDetailsClick(pattern)}
                  onEdit={this.onEdit(pattern)}
                  onPublish={this.onPublish(pattern)}
                />
              </PatternCardWrapper>
            ))}
          </InfiniteScroll>
        ) : (
          <Placeholder
            height={280}
            width={280}
            text={
              PatternStore.search
                ? "No matches found. Please try another search."
                : "No patterns added yet."
            }
          />
        )}
      </Page>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(Pattern)));
